import TraceView from './TraceView';
import { JobDetail } from './jobTypes';
import { Badge } from '@/components/ui/badge';
import { DmsAgentRole, DmsAgentRoleLabels } from '@/constants/ai.enums';
import { Link } from 'react-router-dom';
import ParaLexSummaryPanel from './ParaLexSummaryPanel';
import McpJobAnalysisPanel from './McpJobAnalysisPanel';
// Keep minimal MCP hook imports for cost tracking only
import { useMcpJobAnalysis } from './useMcpJobAnalysis';
import { useMcpJobManager } from './useMcpJobManager';

interface JobDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  job: JobDetail | null;
  onRetry?: () => void;
  onRemove?: () => void;
  onPromote?: () => void;
}

// Document processing job types for identification
const DOCUMENT_JOBS = [
  'DocumentUploadIngestJob',
  'TextExtractionJob', 
  'MetadataExtractionJob',
  'DocumentVectorizationJob',
  'DocumentIngestJob'
];

const isDocumentJob = (jobName?: string) => {
  return jobName && DOCUMENT_JOBS.some(docJob => jobName.includes(docJob));
};

// Document processing pipeline stages
const DOCUMENT_PIPELINE_STAGES = [
  { key: 'upload', label: 'Upload', icon: '📤' },
  { key: 'ingest', label: 'Ingest', icon: '📥' },
  { key: 'extract', label: 'Extract', icon: '📝' },
  { key: 'vectorize', label: 'Vectorize', icon: '🔍' },
  { key: 'complete', label: 'Complete', icon: '✅' }
];

export default function JobDetailModal({
  isOpen,
  onClose,
  job,
  onRetry,
  onRemove,
  onPromote,
}: JobDetailModalProps) {
  // Minimal MCP hook usage for cost tracking section only
  const { hasAnalysis, getAnalysis } = useMcpJobAnalysis();
  const { canUseMcp } = useMcpJobManager();

  if (!isOpen || !job) return null;

  const isDocJob = isDocumentJob(job.name);
  const traceId = job.data?._meta?.traceId;
  const traceGroupId =
    job.data?._meta?.trace_group_id ||
    job.data?.trace_group_id ||
    job.data?.traceGroupId;

  const agentRole =
    job.data?._meta?.agent_role ||
    job.data?.agent_role ||
    job.data?.agentRole;

  // Document-specific metadata
  const documentId = job.data?._meta?.documentId || job.data?.documentId;
  const versionId = job.data?._meta?.versionId || job.data?.versionId;
  const fileType = job.data?._meta?.fileType || job.data?.fileType;
  const fileName = job.data?._meta?.fileName || job.data?.fileName || job.data?._meta?.filename;
  const fileSize = job.data?._meta?.fileSize || job.data?.fileSize;
  const realmId = job.data?._meta?.realmId || job.data?.realmId;

  const roleColorMap: Record<DmsAgentRole, 'success' | 'destructive' | 'secondary' | 'outline'> = {
    PLANNER: 'secondary',
    RETRIEVER: 'secondary',
    SUMMARIZER: 'success',
    CLASSIFIER: 'outline',
    PARSER: 'outline',
    POSTPROCESSOR: 'outline',
    OTHER: 'outline',
  };

  const resolvedRole = agentRole?.toUpperCase() as DmsAgentRole;

  const getCurrentStage = () => {
    if (!job.name) return 0;
    if (job.name.includes('Upload')) return 0;
    if (job.name.includes('Ingest')) return 1;
    if (job.name.includes('Extraction')) return 2;
    if (job.name.includes('Vectorization')) return 3;
    if (job.status === 'completed') return 4;
    return 1;
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return null;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const currentStage = getCurrentStage();

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center">
      <div className={`rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6 ${
        isDocJob 
          ? 'bg-gradient-to-br from-green-50 to-white dark:from-green-900/10 dark:to-gray-850 border border-green-200 dark:border-green-800' 
          : 'bg-white dark:bg-gray-850'
      }`}>
        <div className="flex justify-between items-center border-b border-gray-200 dark:border-gray-700 pb-3 mb-4">
          <div className="flex items-center gap-3">
            {isDocJob && <span className="text-2xl">📄</span>}
            <div>
              <h2 className={`text-xl font-semibold ${
                isDocJob 
                  ? 'text-green-800 dark:text-green-200' 
                  : 'text-gray-900 dark:text-white'
              }`}>
                {isDocJob ? 'Document Job Details' : 'Job Details'}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {job.name || 'Unnamed'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-900 dark:hover:text-white"
            aria-label="Close job detail modal"
          >
            ✕
          </button>
        </div>

        {/* Document Processing Timeline */}
        {isDocJob && (
          <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
            <h3 className="text-sm font-semibold text-green-800 dark:text-green-200 mb-3">
              📋 Document Processing Pipeline
            </h3>
            <div className="flex items-center justify-between">
              {DOCUMENT_PIPELINE_STAGES.map((stage, index) => (
                <div key={stage.key} className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg border-2 ${
                    index <= currentStage
                      ? 'bg-green-500 border-green-500 text-white'
                      : 'bg-gray-200 border-gray-300 text-gray-500 dark:bg-gray-700 dark:border-gray-600'
                  }`}>
                    {stage.icon}
                  </div>
                  <span className={`text-xs mt-1 ${
                    index <= currentStage
                      ? 'text-green-700 dark:text-green-300 font-medium'
                      : 'text-gray-500 dark:text-gray-400'
                  }`}>
                    {stage.label}
                  </span>
                  {index < DOCUMENT_PIPELINE_STAGES.length - 1 && (
                    <div className={`absolute w-8 h-0.5 mt-5 ml-10 ${
                      index < currentStage 
                        ? 'bg-green-500' 
                        : 'bg-gray-300 dark:bg-gray-600'
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 🤖 ParaLex System Agent Analysis - Available for ALL jobs */}
        <ParaLexSummaryPanel 
          job={job}
          isVisible={true}
          onAnalysisComplete={(analysis) => {
            console.log('[JobDetailModal] ParaLex analysis completed:', analysis);
          }}
          onAnalysisError={(error) => {
            console.error('[JobDetailModal] ParaLex analysis error:', error);
          }}
        />

        {/* MCP Analysis Panel - Unified approach for all MCP-enabled scenarios */}
        <McpJobAnalysisPanel
          job={job}
          isVisible={true}
          onAnalysisComplete={(analysis) => {
            console.log('[JobDetailModal] MCP analysis completed:', analysis);
          }}
          onAnalysisError={(error) => {
            console.error('[JobDetailModal] MCP analysis error:', error);
          }}
        />
        
        {/* 🚀 NEW: MCP Cost Tracking (Phase 3) */}
        {canUseMcp && hasAnalysis(job.id) && (
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 shadow">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  💰 Analysis Cost Tracking
                </h3>
                <Badge variant="outline">
                  AI Insights
                </Badge>
              </div>
            </div>
            
            <div className="p-4">
              {(() => {
                const analysis = getAnalysis(job.id);
                if (analysis?.costMetrics) {
                  return (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3 border border-purple-200 dark:border-purple-800">
                        <div className="text-sm text-purple-700 dark:text-purple-300">Analysis Cost</div>
                        <div className="text-lg font-bold text-purple-800 dark:text-purple-200">
                          ${analysis.costMetrics.totalCost.toFixed(4)}
                        </div>
                      </div>
                      <div className="bg-lex-accent-soft rounded-lg p-3 border border-lex-accent">
                        <div className="text-sm text-lex-accent">Tokens Used</div>
                        <div className="text-lg font-bold text-lex-accent">
                          {analysis.costMetrics.tokensUsed.toLocaleString()}
                        </div>
                      </div>
                      <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 border border-green-200 dark:border-green-800">
                        <div className="text-sm text-green-700 dark:text-green-300">Processing Time</div>
                        <div className="text-lg font-bold text-green-800 dark:text-green-200">
                          {analysis.costMetrics.processingTimeMs}ms
                        </div>
                      </div>
                      <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3 border border-amber-200 dark:border-amber-800">
                        <div className="text-sm text-amber-700 dark:text-amber-300">Model Used</div>
                        <div className="text-sm font-medium text-amber-800 dark:text-amber-200">
                          {analysis.costMetrics.modelUsed || 'Unknown'}
                        </div>
                      </div>
                    </div>
                  );
                } else {
                  return (
                    <div className="text-center text-gray-500 dark:text-gray-400 py-4">
                      <p>Cost tracking data not available for this analysis.</p>
                      <p className="text-xs mt-1">Newer analyses include detailed cost breakdowns.</p>
                    </div>
                  );
                }
              })()}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Basic Job Information */}
          <div className="space-y-4 text-sm text-gray-800 dark:text-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
              ⚙️ Job Information
            </h3>
            
            <div>
              <strong>Job ID:</strong>{' '}
              <span className="font-mono text-xs bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                {job.id}
              </span>
              <Link
                to={`/system-monitor/job-trace/${job.id}`}
                className="text-lex-accent hover:underline ml-2"
              >
                View Trace
              </Link>
            </div>
            
            <div>
              <strong>Status:</strong>{' '}
              <Badge variant={
                job.status === 'completed' ? 'success' : 
                job.status === 'failed' ? 'destructive' : 
                'secondary'
              }>
                {job.status}
              </Badge>
            </div>
            
            <div><strong>Attempts:</strong> {job.attempts}</div>

            {traceId && (
              <div>
                <strong>Trace ID:</strong>{' '}
                <span className="font-mono text-xs bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                  {traceId}
                </span>
              </div>
            )}

            {traceGroupId && (
              <div>
                <strong>Trace Group:</strong>{' '}
                <a
                  href={`/system-monitor/trace-group/${traceGroupId}`}
                  className="text-lex-accent hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {traceGroupId.slice(0, 8)}...
                </a>
              </div>
            )}

            {agentRole && (
              <div>
                <strong>Agent Role:</strong>{' '}
                <Badge variant={roleColorMap[resolvedRole] || 'outline'}>
                  {DmsAgentRoleLabels[resolvedRole] || agentRole}
                </Badge>
              </div>
            )}
          </div>

          {/* Document-specific Information */}
          {isDocJob && (
            <div className="space-y-4 text-sm text-gray-800 dark:text-gray-100">
              <h3 className="text-lg font-semibold text-green-800 dark:text-green-200 border-b border-green-200 dark:border-green-700 pb-2">
                📄 Document Information
              </h3>
              
              {documentId && (
                <div>
                  <strong>Document ID:</strong>{' '}
                  <span className="font-mono text-xs bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded">
                    {documentId}
                  </span>
                  <a
                    href={`/chat?documentId=${documentId}`}
                    className="text-green-600 dark:text-green-400 hover:underline ml-2"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View in Chat
                  </a>
                </div>
              )}

              {versionId && (
                <div>
                  <strong>Version ID:</strong>{' '}
                  <span className="font-mono text-xs bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded">
                    {versionId}
                  </span>
                </div>
              )}

              {fileName && (
                <div>
                  <strong>File Name:</strong>{' '}
                  <span className="text-green-700 dark:text-green-300 font-medium">
                    {fileName}
                  </span>
                </div>
              )}

              {fileType && (
                <div>
                  <strong>File Type:</strong>{' '}
                  <Badge variant="outline">
                    {fileType.toUpperCase()}
                  </Badge>
                </div>
              )}

              {fileSize && (
                <div>
                  <strong>File Size:</strong> {formatFileSize(fileSize)}
                </div>
              )}

              {realmId && (
                <div>
                  <strong>Realm:</strong>{' '}
                  <span className="font-medium">{realmId}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Error Information */}
        {job.failedReason && (
          <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
            <h4 className="text-red-800 dark:text-red-200 font-semibold mb-2">❌ Failure Information</h4>
            <p className="text-red-700 dark:text-red-300 text-sm">{job.failedReason}</p>
          </div>
        )}

        {/* Stack Trace */}
        {Array.isArray(job.stacktrace) && job.stacktrace.length > 0 && (
          <div className="mt-6">
            <h4 className="text-gray-900 dark:text-white font-semibold mb-2">🔍 Stack Trace</h4>
            <pre className="p-3 bg-gray-100 dark:bg-gray-800 rounded text-xs whitespace-pre-wrap overflow-x-auto max-h-40">
              {job.stacktrace.join('\n')}
            </pre>
          </div>
        )}

        {/* Raw Job Data */}
        {job.data && (
          <div className="mt-6">
            <details className="group">
              <summary className="cursor-pointer text-gray-900 dark:text-white font-semibold mb-2 hover:text-lex-accent">
                📊 Raw Job Data
                <span className="ml-2 text-xs text-gray-500 group-open:hidden">(click to expand)</span>
              </summary>
              <pre className="mt-2 p-3 bg-gray-100 dark:bg-gray-800 rounded text-xs whitespace-pre-wrap overflow-x-auto max-h-60">
                {JSON.stringify(job.data, null, 2)}
              </pre>
            </details>
          </div>
        )}

        {/* Trace View */}
        {traceId && (
          <div className="mt-6 border-t border-gray-300 dark:border-gray-700 pt-4">
            <TraceView traceId={traceId} />
          </div>
        )}

        {/* Action Buttons */}
        <div className="mt-6 flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          {documentId && isDocJob && (
            <a
              href={`/chat?documentId=${documentId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              💬 Open in Chat
            </a>
          )}
          {onPromote && (
            <button
              onClick={onPromote}
              className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
            >
              ⏫ Promote
            </button>
          )}
          {onRetry && (
            <button
              onClick={onRetry}
              className="px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover transition-colors"
            >
              🔄 Retry
            </button>
          )}
          {onRemove && (
            <button
              onClick={onRemove}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              🗑️ Remove
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
